import { LOCAL_STORAGE_KEYS } from '@bulknutrients/bulk-shared-lib/constants';
import type { BaseBlog } from '@bulknutrients/bulk-shared-lib/interfaces/Blog';
import type { LocalStorageProduct } from '@bulknutrients/bulk-shared-lib/interfaces/LocalStorage';
import type {
  BaseProduct,
  Product,
} from '@bulknutrients/bulk-shared-lib/interfaces/Product';
import {
  addIndexedDBData,
  getIndexedDBStoreData,
  Stores,
  updateIndexedDBData,
} from '@bulknutrients/bulk-shared-lib/lib/local/db';
import {
  UIContext,
  type UIContextInterface,
} from '@components/Context/UIContext/UIContext';
import { logBreadcrumb } from '@lib/logs';
import { useCallback, useContext } from 'react';

export interface ProductStoreInfo {
  product: Product | BaseProduct;
  viewedTime: number;
}

export interface BlogStoreInfo {
  blog: BaseBlog;
  viewedTime: number;
}

export interface PageStoreInfo {
  page: { slug: string; title: string };
  viewedTime: number;
}

export function useLocalStorage() {
  const { updatedRecentlyViewed } = useContext<UIContextInterface>(UIContext);

  const getRecentlyViewedProducts = useCallback(async (): Promise<
    LocalStorageProduct[]
  > => {
    try {
      const localItems = await getIndexedDBStoreData<LocalStorageProduct[]>(
        Stores.keyvaluepairs,
        LOCAL_STORAGE_KEYS.RECENTLY_VIEWED_PRODUCTS
      );
      return localItems || [];
    } catch {
      logBreadcrumb({
        category: 'IndexedDB',
        message: `Tried to get items from ${LOCAL_STORAGE_KEYS.RECENTLY_VIEWED_PRODUCTS}`,
        level: 'error',
      });
      return [];
    }
  }, []);

  const getRecentlyViewedBlogs = useCallback(async (): Promise<
    BlogStoreInfo[]
  > => {
    try {
      const localItems = await getIndexedDBStoreData<BlogStoreInfo[]>(
        Stores.keyvaluepairs,
        LOCAL_STORAGE_KEYS.RECENTLY_VIEWED_BLOGS
      );
      return localItems || [];
    } catch {
      logBreadcrumb({
        category: 'IndexedDB',
        message: `Tried to get items from ${LOCAL_STORAGE_KEYS.RECENTLY_VIEWED_BLOGS}`,
        level: 'error',
      });
      return [];
    }
  }, []);

  const getRecentlyViewedPages = useCallback(async (): Promise<
    PageStoreInfo[]
  > => {
    try {
      const localItems = await getIndexedDBStoreData<PageStoreInfo[]>(
        Stores.keyvaluepairs,
        LOCAL_STORAGE_KEYS.RECENTLY_VIEWED_PAGES
      );
      return localItems || [];
    } catch {
      logBreadcrumb({
        category: 'IndexedDB',
        message: `Tried to get items from ${LOCAL_STORAGE_KEYS.RECENTLY_VIEWED_PAGES}`,
        level: 'error',
      });
      return [];
    }
  }, []);

  const setRecentlyViewedProduct = useCallback(
    async (storeInfo: ProductStoreInfo) => {
      try {
        const current = await getRecentlyViewedProducts();

        if (current.length > 0) {
          let modified: LocalStorageProduct[];
          const existing = current.findIndex(
            (item) => item.product.entityId === storeInfo.product.entityId
          );

          // Set new store data
          if (existing > -1) {
            // already in there, update time
            current[existing] = storeInfo;
            modified = [...current];
          } else {
            modified = [...current, storeInfo];
          }

          // Newest view at the top
          modified.sort((a, b) => (a.viewedTime < b.viewedTime ? 1 : -1));

          // trim the store to most recent 10
          if (modified.length > 10) {
            modified.splice(10);
          }
          await updateIndexedDBData(
            Stores.keyvaluepairs,
            LOCAL_STORAGE_KEYS.RECENTLY_VIEWED_PRODUCTS,
            modified
          );
          updatedRecentlyViewed();
        } else {
          await addIndexedDBData(
            Stores.keyvaluepairs,
            LOCAL_STORAGE_KEYS.RECENTLY_VIEWED_PRODUCTS,
            [storeInfo]
          );
          updatedRecentlyViewed();
        }
      } catch {
        logBreadcrumb({
          category: 'IndexedDB',
          message: `Tried to store item in ${LOCAL_STORAGE_KEYS.RECENTLY_VIEWED_PRODUCTS}`,
          level: 'error',
        });
      }
    },
    [getRecentlyViewedProducts, updatedRecentlyViewed]
  );

  const setRecentlyViewedBlog = useCallback(
    async (storeInfo: BlogStoreInfo) => {
      try {
        const current = await getRecentlyViewedBlogs();

        if (current && current.length > 0) {
          let modified: BlogStoreInfo[];
          const existing = current.findIndex(
            (item) => item.blog.slug === storeInfo.blog.slug
          );

          // Set new store data
          if (existing > -1) {
            // already in there, update time
            current[existing] = storeInfo;
            modified = [...current];
          } else {
            modified = [...current, storeInfo];
          }

          // Newest view at the top
          modified.sort((a, b) => (a.viewedTime < b.viewedTime ? 1 : -1));

          // trim the store to most recent 10
          if (modified.length > 10) {
            modified.splice(10);
          }
          await updateIndexedDBData(
            Stores.keyvaluepairs,
            LOCAL_STORAGE_KEYS.RECENTLY_VIEWED_BLOGS,
            modified
          );
          updatedRecentlyViewed();
        } else {
          await addIndexedDBData(
            Stores.keyvaluepairs,
            LOCAL_STORAGE_KEYS.RECENTLY_VIEWED_BLOGS,
            [storeInfo]
          );
          updatedRecentlyViewed();
        }
      } catch {
        logBreadcrumb({
          category: 'IndexedDB',
          message: `Tried to store item in ${LOCAL_STORAGE_KEYS.RECENTLY_VIEWED_BLOGS}`,
          level: 'error',
        });
      }
    },
    [getRecentlyViewedBlogs, updatedRecentlyViewed]
  );

  const setRecentlyViewedPage = useCallback(
    async (storeInfo: PageStoreInfo) => {
      try {
        const current = await getRecentlyViewedPages();

        if (current && current.length > 0) {
          let modified: PageStoreInfo[];
          const existing = current.findIndex(
            (item) => item.page.slug === storeInfo.page.slug
          );

          // Set new store data
          if (existing > -1) {
            // already in there, update time
            current[existing] = storeInfo;
            modified = [...current];
          } else {
            modified = [...current, storeInfo];
          }

          // Newest view at the top
          modified.sort((a, b) => (a.viewedTime < b.viewedTime ? 1 : -1));

          // trim the store to most recent 10
          if (modified.length > 10) {
            modified.splice(10);
          }
          await updateIndexedDBData(
            Stores.keyvaluepairs,
            LOCAL_STORAGE_KEYS.RECENTLY_VIEWED_PAGES,
            modified
          );
          updatedRecentlyViewed();
        } else {
          await addIndexedDBData(
            Stores.keyvaluepairs,
            LOCAL_STORAGE_KEYS.RECENTLY_VIEWED_PAGES,
            [storeInfo]
          );
          updatedRecentlyViewed();
        }
      } catch {
        logBreadcrumb({
          category: 'IndexedDB',
          message: `Tried to store item in ${LOCAL_STORAGE_KEYS.RECENTLY_VIEWED_PAGES}`,
          level: 'error',
        });
      }
    },
    [getRecentlyViewedPages, updatedRecentlyViewed]
  );

  return {
    getRecentlyViewedProducts,
    getRecentlyViewedBlogs,
    getRecentlyViewedPages,
    setRecentlyViewedProduct,
    setRecentlyViewedBlog,
    setRecentlyViewedPage,
  };
}
